export type PoleId =
  | 'growth'
  | 'corporateGovernance'
  | 'serviceOps'
  | 'solutionsPrisme'
  | 'programmingAI'
  | 'cloudLicences';

export type PersonType = 'executive' | 'vp' | 'director' | 'owner' | 'leader';

export interface Person {
  id:
    | 'dany'
    | 'francois'
    | 'yannick'
    | 'jp'
    | 'francis'
    | 'eh'
    | 'jm'
    | 'vincent'
    | 'edurand'
    | 'rdelisle'
    | 'antoine'
    | 'rosalie'
    | 'melissa'
    | 'lah'
    | 'guillaume';
  name: string;
  title: string;
  poleId: PoleId;
  poleLabel: string;
  type: PersonType;
  level: 1 | 2 | 3;
  avatar?: string;
  tagline: string;
  whyRole: string;
  summary: {
    mandat: string[];
    responsibilities: string[];
    kpis: string[];
    collaborations: string[];
  };
  details: {
    mandatEsprit: string[];
    responsibilitiesRaci: {
      responsible: string[];
      accountable: string[];
      consulted: string[];
    };
    objectives12Months: string[];
    kpisCadence: string[];
    guardrails: string[];
    interfaces: string[];
  };
}

export interface HierarchicalLink {
  from: Person['id'];
  to: Person['id'];
}

export interface FunctionalLink {
  from: Person['id'];
  to: Person['id'];
}

export interface Committee {
  id: 'comiteGouvernanceTI';
  name: string;
  presidentId: Person['id'];
  memberIds: Person['id'][];
  arbitrageFinalId: Person['id'];
  mandate: string;
  rules: string[];
}

export const persons: Person[] = [
  {
    id: 'dany',
    name: 'Dany Bourassa',
    title: 'Président & PDG',
    poleId: 'corporateGovernance',
    poleLabel: 'Gouvernance globale & culture',
    type: 'executive',
    level: 1,
    avatar: '/DB.png',
    tagline: "Arbitre stratégique, culture et valeur long terme de BZ.",
    whyRole:
      "Tenir ensemble clients, équipes, finances et stratégie pour que chaque décision TI crée de la valeur durable.",
    summary: {
      mandat: [
        'Garder BZ alignée sur une vision simple et compréhensible.',
        "Assurer que chaque dollar TI investi par les clients rapporte.",
        'Soutenir les leaders pour exécuter sans zone grise.',
      ],
      responsibilities: [
        'Assumer Dany CA/CE et trancher les arbitrages majeurs.',
        'Arbitrer les décisions qui engagent la réputation ou la résilience de BZ.',
        'Porter la vision PRIsME et l’IA comme leviers de valeur, pas comme gadgets.',
        'S’assurer que la gouvernance reste vivante, simple et respectée.',
      ],
      kpis: [
        'Satisfaction globale des clients CE et stratégiques.',
        'Croissance rentable annuelle (revenus vs marge).',
        'Taux d’adoption des cadres de gouvernance TI par les leaders (PRIsME comme point central de la transformation de l’approche client).',
      ],
      collaborations: [
        'Travaille en binôme avec François sur la rigueur et la résilience.',
        'Pilote directement la croissance, le marché et les arbitrages transversaux.',
        'S’appuie sur les directeurs (JP, Francis, Éric D., Richard) pour l’exécution TI.',
      ],
    },
    details: {
      mandatEsprit: [
        'Protéger l’ADN de BZ : humain, TI, gouvernance et rentabilité au service du client.',
        'Rendre les décisions Dany CA/CE simples, explicites et exécutables.',
      ],
      responsibilitiesRaci: {
        responsible: ['Animation et décisions finales Dany CA/CE.', 'Validation des grands arbitrages TI & business.'],
        accountable: [
          'Vision et stratégie globale de BZ.',
          'Qualité globale de la gouvernance Dany CA/CE.',
        ],
        consulted: ['Décisions majeures des directions (gouvernance, TI, marché, finances).'],
      },
      objectives12Months: [
        'Augmenter la clarté des décisions Dany CA/CE (documentation systématique).',
        'Ancrer PRIsME comme langage commun de pilotage dans tous les comités clés.',
      ],
      kpisCadence: [
        'Revue mensuelle des décisions Dany CA/CE et de leur exécution.',
        'Baromètre de satisfaction leaders / gestionnaires face à la gouvernance.',
      ],
      guardrails: [
        'Ne pas multiplier les exceptions qui complexifient la gouvernance.',
        'Ne pas sacrifier la confiance client pour un gain court terme.',
      ],
      interfaces: [
        'Interface avec le CA pour les sujets de vision et de risque stratégique.',
        'Interface avec tous les VP/directeurs pour valider les décisions structurantes.',
      ],
    },
  },
  {
    id: 'francois',
    name: 'François Delisle',
    title: 'VP Gouvernance corporative',
    poleId: 'corporateGovernance',
    poleLabel: 'Finance, RH, conformité, risque',
    type: 'vp',
    level: 2,
    avatar: '/FP.png',
    tagline: 'Protège les acquis et la valeur de BZ par la rigueur et la sécurité.',
    whyRole:
      "S’assurer que la croissance de BZ repose sur des bases financières, humaines et de conformité solides et maîtrisées.",
    summary: {
      mandat: [
        'Surveillance et compréhension de la santé financière et de la conformité de BZ.',
        'Faire vivre la gouvernance corporative au quotidien.',
        'Donner une lecture claire des risques à Dany CA/CE.',
      ],
      responsibilities: [
        'Superviser Finance, Administration & RH.',
        'Mettre en place des contrôles internes simples et efficaces.',
        'Challenger les décisions Dany CA/CE sous l’angle risque & conformité.',
      ],
      kpis: [
        'Qualité des contrôles et audits (interne/externe).',
        'Santé de la trésorerie et du fonds de roulement.',
        'Stabilité RH sur les postes clés.',
      ],
      collaborations: [
        'Travaille avec Mélissa sur les banques, fiscalité et contrôles.',
        'Travaille avec LAH sur les flux administratifs et RH.',
        'Collabore avec Dany pour les arbitrages risque / croissance.',
      ],
    },
    details: {
      mandatEsprit: [
        'Être la conscience “risque & conformité” auprès de Dany CA/CE.',
        'Traduire la stratégie en règles simples qui protègent BZ sans la paralyser.',
      ],
      responsibilitiesRaci: {
        responsible: ['Mise en place des cadres de gouvernance corporative.', 'Suivi des risques majeurs.'],
        accountable: [
          'Conformité légale et fiscale.',
          'Robustesse des contrôles financiers.',
          'Projets spéciaux stratégiques – vérifications diligentes avant décision (due diligence).',
        ],
        consulted: ['Décisions d’investissement majeures.', 'Entrées / sorties de marchés ou offres structurantes.'],
      },
      objectives12Months: [
        'Clarifier et documenter les principaux flux financiers et RH.',
        'Mettre en place un tableau de bord simple pour Dany CA/CE sur les indicateurs clés.',
      ],
      kpisCadence: [
        'Revue trimestrielle des risques avec Dany CA/CE.',
        'Suivi mensuel des indicateurs financiers structurants.',
      ],
      guardrails: [
        'Éviter la bureaucratie inutile qui ralentit les opérations.',
        "Limiter les compromis sur l'éthique ou la conformité.",
      ],
      interfaces: [
        'Interface avec les banques et partenaires financiers.',
        'Interface avec les conseillers légaux/fiscaux.',
      ],
    },
  },
  {
    id: 'jp',
    name: 'Jean-Pierre Leclerc',
    title: 'Directeur Service TI & Opérations',
    poleId: 'serviceOps',
    poleLabel: 'Service TI & Opérations',
    type: 'director',
    level: 2,
    avatar: '/JP.png',
    tagline: 'Tiens la promesse au quotidien : SLA, N1/N2/SAC et moteur récurrent.',
    whyRole: 'Assurer un service TI stable, prévisible et récurrent qui protège la confiance client.',
    summary: {
      mandat: [
        'Opérer le moteur récurrent TI (N1/N2/SAC).',
        'Assurer le respect des SLA et la satisfaction quotidienne.',
        'Remonter les signaux faibles du terrain au CE.',
      ],
      responsibilities: [
        'Pilotage des équipes de support TI.',
        'Gestion des incidents majeurs.',
        'Coordination avec les autres directions pour les escalades.',
      ],
      kpis: [
        'Respect des SLA clés.',
        'Satisfaction client sur le support.',
        'Stabilité des opérations (incidents majeurs).',
      ],
      collaborations: [
        'Travaille avec Francis pour les escalades N3.',
        'Travaille avec Éric D. pour les automatisations support.',
        'Collabore avec Richard pour la couche infra / cloud.',
      ],
    },
    details: {
      mandatEsprit: [
        'Être le moteur TI qui ne lâche pas, même quand ça brasse.',
        'Donner de la prévisibilité au CE et aux clients sur la qualité du service.',
      ],
      responsibilitiesRaci: {
        responsible: ['Opérations quotidiennes du support TI.', 'Gestion des SLA.'],
        accountable: ['Qualité globale du service TI.', 'Expérience support pour les clients.'],
        consulted: ['Décisions sur les changements majeurs d’outils ou de processus TI.'],
      },
      objectives12Months: [
        'Stabiliser les principaux SLA et les documenter clairement.',
        'Renforcer la boucle de feedback entre support, projets et PRIsME.',
      ],
      kpisCadence: [
        'Revue hebdo des incidents / SLA.',
        'Revue mensuelle avec le CE des enjeux majeurs de service.',
      ],
      guardrails: [
        'Ne pas traiter les symptômes sans adresser les causes racines.',
        'Ne pas surcharger les équipes au détriment de la qualité.',
      ],
      interfaces: [
        'Interface principale avec les clients pour les opérations TI.',
        'Interface avec les autres directeurs pour les escalades et changements.',
      ],
    },
  },
  {
    id: 'francis',
    name: 'Francis Marquis',
    title: 'Directeur Solutions d’affaires & Cybersécurité + PRIsME',
    poleId: 'solutionsPrisme',
    poleLabel: 'Solutions d’affaires & Cybersécurité + PRIsME',
    type: 'director',
    level: 2,
    avatar: '/FM.png',
    tagline: 'Pilote le rendement de l’équipe Solutions & Cybersécurité et la qualité PRIsME côté client.',
    whyRole:
      'Assurer que chaque heure de son équipe génère de la valeur mesurable pour les clients et pour BZ, en s’appuyant sur PRIsME comme cerveau central.',
    summary: {
      mandat: [
        'Piloter le département Solutions d’affaires & Cybersécurité (gestion d’équipe, priorités, qualité).',
        'Garder le focus sur les résultats: rendement $ du département et satisfaction client 100%.',
        'Ancrer tout le travail de l’équipe dans PRIsME (Master BZ et PRIsME Client).',
      ],
      responsibilities: [
        'Organiser et prioriser le travail de l’équipe ( capacité, files, escalades ) sans se disperser.',
        'Suivre et améliorer le rendement économique du département (avec coaching chiffres par JP).',
        'S’assurer que chaque client a une trajectoire PRIsME claire et tenue à jour.',
      ],
      kpis: [
        'Adoption et bon usage des outils par l’équipe (checklists, PRIsME, outils TI).',
        'Rendement $ du département (marge/heure, productivité) en amélioration continue.',
        'Satisfaction client 100 % sur le portefeuille Solutions & Cybersécurité.',
        'Adoption PRIsME 100 % sur les clients couverts par le département.',
      ],
      collaborations: [
        'Travaille avec JP pour le dispatch des revenus et le coaching sur les chiffres (sans gérer les ventes).',
        'Travaille avec Éric H. pour garder PRIsME Master BZ clair et applicable par son équipe.',
        'Collabore avec les directeurs TI pour que les décisions client restent simples, sécuritaires et rentables.',
      ],
    },
    details: {
      mandatEsprit: [
        'Être le pilote calme et focus du département Solutions d’affaires & Cybersécurité.',
        'Mettre PRIsME au centre : tout ce qui est fait pour les clients doit se refléter dans PRIsME.',
        'Protéger la simplicité pour son équipe : moins de dispersion, plus de profondeur sur les bons sujets.',
      ],
      responsibilitiesRaci: {
        responsible: [
          'Pilotage opérationnel du département (priorités, capacité, qualité livrée).',
          'Rendement $ du département (en collaboration étroite avec JP).',
          'Alimentation continue de PRIsME Client et PRIsME Master BZ par son équipe.',
        ],
        accountable: [
          'Qualité globale et stabilité des services Solutions & Cybersécurité livrés aux clients.',
          'Respect des standards de cybersécurité et des pratiques PRIsME dans son équipe.',
        ],
        consulted: [
          'Décisions CE touchant l’offre Solutions d’affaires & Cybersécurité.',
          'Décisions CE touchant la structure et la cadence PRIsME côté clients.',
        ],
      },
      objectives12Months: [
        'Stabiliser et clarifier le fonctionnement interne du département (rôles, files, rituels).',
        'Atteindre une adoption PRIsME 100 % sur les clients gérés par son équipe.',
        'Mettre en place un tableau de bord simple : rendement $, satisfaction client, adoption PRIsME.',
      ],
      kpisCadence: [
        'Revue mensuelle avec JP sur les chiffres du département (sans gérer les ventes).',
        'Revue régulière des PRIsME Clients pour s’assurer qu’ils sont à jour et utiles.',
        'Boucles de feedback trimestrielles avec les clients clés sur la qualité et la clarté des solutions.',
      ],
      guardrails: [
        'Ne pas se disperser sur les ventes ou le marketing : rester centré sur le rendement de l’équipe.',
        'Ne pas sacrifier la sécurité ou la qualité pour du court terme financier.',
        'Ne pas laisser PRIsME devenir un “extra” : c’est le canal officiel de ce que fait l’équipe.',
      ],
      interfaces: [
        'Interface avec JP pour le dispatch des revenus, la lecture des chiffres et les arbitrages de capacité.',
        'Interface avec Éric H. pour les évolutions PRIsME qui impactent les clients.',
        'Interface avec les autres directeurs TI pour la répartition du travail et les escalades complexes.',
      ],
    },
  },
  {
    id: 'eh',
    name: 'Éric Hains',
    title: 'Owner PRIsME',
    poleId: 'solutionsPrisme',
    poleLabel: 'Cadre & gouvernance PRIsME',
    type: 'owner',
    level: 3,
    avatar: '/EH.png',
    tagline: 'Garde le cadre PRIsME vivant, cohérent et utile.',
    whyRole: 'Assurer que PRIsME reste un cadre utilisable, désirable et vendable pour BZ et ses clients.',
    summary: {
      mandat: [
        'Définir et faire évoluer le cadre PRIsME.',
        'Soutenir les directions dans l’utilisation concrète de PRIsME.',
        'Assurer la cohérence entre PRIsME et les autres cadres internes.',
      ],
      responsibilities: [
        'Animation des contenus PRIsME.',
        'Accompagnement des équipes sur l’utilisation du cadre.',
        'Contribution aux comités de gouvernance TI.',
      ],
      kpis: [
        'Taux d’adoption de PRIsME par les équipes.',
        'Qualité perçue du cadre par les leaders.',
        'Nombre de décisions structurées à partir de PRIsME.',
      ],
      collaborations: [
        'Travaille avec Francis pour intégrer PRIsME aux solutions.',
        'Travaille avec JP pour l’appliquer aux opérations.',
        'Collabore avec Rosalie pour le positionnement PRIsME.',
      ],
    },
    details: {
      mandatEsprit: [
        'Être le gardien du sens et de la cohérence de PRIsME.',
        'Rendre PRIsME concret pour les décisions du CE et des directions.',
      ],
      responsibilitiesRaci: {
        responsible: ['Évolution du cadre PRIsME.', 'Clarté et pédagogie autour du cadre.'],
        accountable: ['Qualité globale du référentiel PRIsME.', 'Cohérence avec la stratégie BZ.'],
        consulted: ['Décisions CE touchant la gouvernance ou les standards PRIsME.'],
      },
      objectives12Months: [
        'Simplifier quelques vues clés de PRIsME pour le CE.',
        'Renforcer les liens entre PRIsME et les comités existants.',
      ],
      kpisCadence: [
        'Revue périodique des usages de PRIsME.',
        'Feedback régulier des leaders sur le cadre.',
      ],
      guardrails: [
        'Ne pas transformer PRIsME en usine à gaz.',
        'Ne pas se couper du terrain et des réalités opérationnelles.',
      ],
      interfaces: [
        'Interface avec les directions pour l’implantation de PRIsME.',
        'Interface avec le marketing pour le discours PRIsME.',
      ],
    },
  },
  {
    id: 'jm',
    name: 'Jean-Michel Caouette',
    title: "Expert solution d'affaire",
    poleId: 'solutionsPrisme',
    poleLabel: 'Delivery & intégration PRIsME',
    type: 'director',
    level: 3,
    avatar: '/JMC.png',
    tagline: 'Pont N2↔N3, adoption PRIsME et formation PRIME.',
    whyRole: 'Faire vivre PRIsME dans la livraison, la formation et l’intégration chez les clients.',
    summary: {
      mandat: [
        'Assurer l’intégration concrète de PRIsME dans les mandats.',
        'Aider les équipes à passer de la théorie PRIsME à la pratique.',
        'Soutenir la formation PRIME chez les clients et employés.',
      ],
      responsibilities: [
        'Pilotage des livraisons PRIsME.',
        'Coordination entre support, projets et cadre PRIsME.',
        'Soutien aux gestionnaires dans l’utilisation du cadre.',
      ],
      kpis: [
        'Taux de complétion des implantations PRIsME.',
        'Satisfaction client sur les mandats PRIsME.',
        'Progression de maturité PRIsME chez les clients clés.',
      ],
      collaborations: [
        'Travaille avec JP pour les ponts N2↔N3.',
        'Travaille avec Éric H. sur le contenu PRIsME.',
        'Collabore avec Vincent pour le feedback terrain.',
      ],
    },
    details: {
      mandatEsprit: [
        'Être la “colle” entre le cadre PRIsME et la réalité terrain.',
        'Rendre les implantations PRIsME prévisibles et apprenantes.',
      ],
      responsibilitiesRaci: {
        responsible: ['Delivery des mandats PRIsME.', 'Formation PRIME sur le terrain.'],
        accountable: ['Qualité globale des implantations PRIsME.', 'Satisfaction client sur ces mandats.'],
        consulted: ['Ajustements au cadre ou à la méthode PRIsME.'],
      },
      objectives12Months: [
        'Structurer un parcours type d’implantation PRIsME.',
        'Mettre en place une boucle de feedback terrain systématique.',
      ],
      kpisCadence: [
        'Suivi mensuel des mandats PRIsME.',
        'Revue périodique des apprentissages par le CE ou comité TI.',
      ],
      guardrails: [
        'Ne pas improviser les implantations au cas par cas.',
        'Ne pas déconnecter les formations de la réalité client.',
      ],
      interfaces: [
        'Interface avec les clients pour les implantations PRIsME.',
        'Interface avec les directions internes pour la capacité et la priorisation.',
      ],
    },
  },
  {
    id: 'vincent',
    name: 'Vincent Bourassa',
    title: 'Leadership terrain',
    poleId: 'serviceOps',
    poleLabel: 'Terrain & feedback opérations',
    type: 'leader',
    level: 3,
    avatar: '/VB.png',
    tagline: 'Relie le terrain au CE : exécution et feedback en continu.',
    whyRole: 'Donner une lecture terrain honnête et continue au CE sur ce qui fonctionne ou non.',
    summary: {
      mandat: [
        'Remonter les signaux faibles du terrain.',
        'Aider à faire atterrir les décisions CE sur le plancher.',
        'Soutenir les équipes dans l’exécution quotidienne.',
      ],
      responsibilities: [
        'Observation et feedback sur les opérations.',
        'Soutien aux gestionnaires dans la mise en œuvre.',
        'Contribution aux comités TI sur la réalité terrain.',
      ],
      kpis: [
        'Qualité du feedback terrain utilisé par le CE.',
        'Améliorations concrètes déclenchées par ces feedbacks.',
        'Climat opérationnel sur le terrain.',
      ],
      collaborations: [
        'Travaille avec JP sur les enjeux d’opérations.',
        'Travaille avec JM sur les mandats PRIsME.',
        'Collabore avec les autres directions selon les dossiers.',
      ],
    },
    details: {
      mandatEsprit: [
        'Être les yeux et les oreilles du CE sur le terrain.',
        'Faire remonter les irritants avant qu’ils ne deviennent des crises.',
      ],
      responsibilitiesRaci: {
        responsible: ['Feedback terrain structuré.', 'Soutien à l’exécution des décisions CE.'],
        accountable: ['Qualité de la boucle terrain ↔ CE.'],
        consulted: ['Décisions touchant directement l’exécution opérationnelle.'],
      },
      objectives12Months: [
        'Mettre en place un canal clair de remontée d’enjeux terrain.',
        'Contribuer à quelques améliorations majeures basées sur le terrain.',
      ],
      kpisCadence: [
        'Fréquence des feedbacks traités en CE ou en comité.',
        'Nombre d’améliorations issues du terrain.',
      ],
      guardrails: [
        'Ne pas filtrer les mauvaises nouvelles.',
        'Ne pas se substituer aux gestionnaires en place.',
      ],
      interfaces: [
        'Interface avec JP et les équipes de support.',
        'Interface avec JM et les équipes projets PRIsME.',
      ],
    },
  },
  {
    id: 'edurand',
    name: 'Éric Durand',
    title: 'Directeur Programmation & IA',
    poleId: 'programmingAI',
    poleLabel: 'Programmation & IA',
    type: 'director',
    level: 2,
    avatar: '/ED.png',
    tagline: 'Build, automatisation, agents et intégrations.',
    whyRole:
      'Transformer les idées et cadres de BZ (dont PRIsME) en solutions logicielles, automatisations et IA concrètes.',
    summary: {
      mandat: [
        'Construire et maintenir les solutions logicielles internes et clients.',
        'Exploiter l’IA comme levier de valeur, jamais comme gadget.',
        'Soutenir les autres directions avec des outils et automatisations.',
      ],
      responsibilities: [
        'Pilotage des équipes de développement logiciel et IA.',
        'Qualité et stabilité des livrables logiciels.',
        'Alignement des choix technologiques avec la stratégie CE.',
      ],
      kpis: [
        'Taux de réussite des projets logiciels.',
        'Impact mesuré des automatisations / IA.',
        'Qualité technique (défauts, performance, sécurité).',
      ],
      collaborations: [
        'Travaille avec Richard sur les contraintes de plateforme et de coûts.',
        'Travaille avec Francis sur les architectures applicatives.',
        'Collabore avec JP et JM sur les outils support / PRIsME.',
      ],
    },
    details: {
      mandatEsprit: [
        'Être le bras “build & IA” de BZ.',
        'Faire des choix technos qui servent la stratégie, pas l’inverse.',
      ],
      responsibilitiesRaci: {
        responsible: ['Delivery des projets logiciels et IA.', 'Qualité du code et des pipelines.'],
        accountable: ['Performance et fiabilité des solutions construites.', "Alignement des choix technos avec BZ."],
        consulted: ['Décisions CE touchant les plateformes, langages ou grandes orientations technos.'],
      },
      objectives12Months: [
        'Structurer quelques patterns IA / agents réutilisables.',
        'Réduire le temps de cycle des développements critiques.',
      ],
      kpisCadence: [
        'Suivi régulier des projets clés.',
        'Revue périodique de l’impact des automatisations / IA.',
      ],
      guardrails: [
        'Ne pas multiplier les technologies sans raison.',
        'Ne pas lancer d’initiatives IA déconnectées de la valeur client.',
      ],
      interfaces: [
        'Interface avec les autres directeurs TI.',
        'Interface avec les clients pour les projets logiciels stratégiques.',
      ],
    },
  },
  {
    id: 'guillaume',
    name: 'Guillaume Cloutier',
    title: 'Responsable efficacité opérationnelle programmation',
    poleId: 'programmingAI',
    poleLabel: 'Programmation & IA',
    type: 'leader',
    level: 3,
    avatar: '/GC.png',
    tagline: 'Cadence, efficacité terrain et coordination opérationnelle des programmeurs.',
    whyRole:
      'Aider Éric Durand à transformer les priorités de programmation en exécution efficace, alignée et mesurable.',
    summary: {
      mandat: [
        'Opérer l’efficacité quotidienne de l’équipe de programmation.',
        'Identifier les frictions, blocages et pertes de cadence.',
        'Soutenir les programmeurs pour livrer plus clairement et plus régulièrement.',
      ],
      responsibilities: [
        'Coordination opérationnelle des programmeurs.',
        'Suivi de la cadence, des irritants et des priorités techniques.',
        'Amélioration continue des méthodes de livraison logicielle.',
      ],
      kpis: [
        'Réduction des blocages de programmation.',
        'Prévisibilité de livraison.',
        'Qualité de coordination entre demandes, build et opérations.',
      ],
      collaborations: [
        'Travaille directement avec Éric Durand sur les priorités de programmation.',
        'Travaille avec les équipes projet pour clarifier les demandes.',
        'Remonte au CE les irritants systémiques qui nuisent à la cadence.',
      ],
    },
    details: {
      mandatEsprit: [
        'Être l’opérateur d’efficacité de l’équipe de programmation.',
        'Faire circuler les bonnes priorités sans créer de lourdeur inutile.',
      ],
      responsibilitiesRaci: {
        responsible: ['Coordination opérationnelle des programmeurs.', 'Suivi des blocages et irritants de delivery.'],
        accountable: ['Efficacité opérationnelle de l’équipe de programmation.'],
        consulted: ['Décisions CE touchant la capacité, les cadences ou les méthodes de programmation.'],
      },
      objectives12Months: [
        'Stabiliser une cadence de programmation plus prévisible.',
        'Documenter les frictions récurrentes et les convertir en améliorations simples.',
      ],
      kpisCadence: [
        'Revue hebdo des blocages et priorités.',
        'Suivi mensuel de l’efficacité opérationnelle programmation avec Éric.',
      ],
      guardrails: [
        'Ne pas devenir un goulot de validation.',
        'Ne pas remplacer la responsabilité technique d’Éric; soutenir l’exécution opérationnelle.',
      ],
      interfaces: [
        'Interface avec les programmeurs.',
        'Interface avec Éric Durand et les demandeurs internes.',
      ],
    },
  },
  {
    id: 'rdelisle',
    name: 'Richard Delisle',
    title: 'Directeur Infonuagique / Cloud & Licences',
    poleId: 'cloudLicences',
    poleLabel: 'Infonuagique & Licences',
    type: 'director',
    level: 2,
    avatar: '/RD.png',
    tagline: 'Cloud ops, standards, coûts et licences.',
    whyRole: 'Assurer une infrastructure cloud fiable, sécuritaire et maîtrisée au niveau des coûts et des licences.',
    summary: {
      mandat: [
        'Piloter les opérations cloud et licences.',
        'Optimiser les coûts sans compromettre la qualité.',
        'Standardiser les pratiques infonuagiques chez BZ et chez les clients.',
      ],
      responsibilities: [
        'Gestion des environnements cloud.',
        'Standardisation des patterns d’hébergement.',
        'Suivi des coûts et des contrats de licences.',
      ],
      kpis: [
        'Stabilité des environnements cloud.',
        'Optimisation des coûts cloud / licences.',
        'Respect des standards de sécurité cloud.',
      ],
      collaborations: [
        'Travaille avec JP sur le run infra / support.',
        'Travaille avec Éric D. sur les contraintes de plateforme.',
        'Collabore avec Francis sur les architectures cloud.',
      ],
    },
    details: {
      mandatEsprit: [
        'Être le gardien de la couche infra & licences.',
        'Faire en sorte que les choix cloud soutiennent la stratégie, pas l’inverse.',
      ],
      responsibilitiesRaci: {
        responsible: ['Gestion des environnements infonuagiques.', 'Suivi des licences majeures.'],
        accountable: ['Fiabilité de l’infrastructure.', 'Coûts globaux cloud & licences.'],
        consulted: ['Décisions CE sur les choix d’infrastructure ou de grands contrats cloud/licences.'],
      },
      objectives12Months: [
        'Standardiser quelques patterns cloud BZ.',
        'Mieux visualiser les coûts cloud pour le CE.',
      ],
      kpisCadence: [
        'Revue périodique des coûts et incidents cloud.',
        'Suivi des renouvellements de licences critiques.',
      ],
      guardrails: [
        'Ne pas sous-dimensionner les environnements au détriment de la qualité.',
        'Ne pas engager BZ dans des contrats rigides sans garde-fous.',
      ],
      interfaces: [
        'Interface avec les directeurs TI pour les besoins infra.',
        'Interface avec les fournisseurs cloud / licences.',
      ],
    },
  },
  {
    id: 'antoine',
    name: 'Antoine Giroux',
    title: 'Directeur Ventes',
    poleId: 'growth',
    poleLabel: 'Croissance & Marché',
    type: 'director',
    level: 3,
    avatar: '/AG-transparent.png',
    tagline: 'Transforme les opportunités en mandats gagnant-gagnant.',
    whyRole: 'Construire un pipeline sain et convertir les bonnes opportunités en mandats rentables.',
    summary: {
      mandat: [
        'Piloter l’équipe de vente.',
        'Assurer un pipeline clair et documenté.',
        'Aligner les ventes avec la capacité réelle de BZ.',
      ],
      responsibilities: [
        'Gestion opérationnelle des ventes.',
        'Prévision et suivi des revenus.',
        'Coordination avec les opérations pour la faisabilité.',
      ],
      kpis: [
        'Taux de conversion des opportunités.',
        'Prévisibilité du chiffre d’affaires.',
        'Qualité des mandats signés.',
      ],
      collaborations: [
        'Travaille avec Dany pour la stratégie de développement.',
        'Travaille avec Francis pour la validation technique des promesses.',
        'Collabore avec Antoine pour la coordination des démarrages de mandat.',
      ],
    },
    details: {
      mandatEsprit: [
        'Vendre ce que BZ sait livrer avec excellence.',
        'Garder un pipeline transparent pour le CE et les opérations.',
      ],
      responsibilitiesRaci: {
        responsible: ['Exécution du processus de vente.', 'Prévisions de revenus.'],
        accountable: ['Objectifs de ventes atteints.', 'Qualité du portefeuille d’opportunités.'],
        consulted: ['Décisions sur les clients / mandats stratégiques.'],
      },
      objectives12Months: [
        'Stabiliser un rythme de vente prévisible.',
        'Améliorer la qualification des mandats en amont.',
      ],
      kpisCadence: [
        'Revue hebdo pipeline.',
        'Revue mensuelle des résultats avec le CE.',
      ],
      guardrails: [
        'Ne pas accepter de mandats non alignés avec la capacité.',
        'Ne pas sur-promettre au détriment du terrain.',
      ],
      interfaces: [
        'Interface avec les opérations pour la capacité.',
        'Interface avec le marketing pour les campagnes.',
      ],
    },
  },
  {
    id: 'rosalie',
    name: 'Rosalie Émond',
    title: 'Spécialiste Marketing & Positionnement',
    poleId: 'growth',
    poleLabel: 'Croissance & Marché',
    type: 'leader',
    level: 3,
    avatar: '/RE.png',
    tagline: 'Rend BZ visible, compréhensible et désirable.',
    whyRole: 'Positionner BZ et PRIsME de façon claire pour attirer les bons clients et talents.',
    summary: {
      mandat: [
        'Mettre BZ et PRIsME sur le radar des bons clients.',
        'Clarifier le message et la proposition de valeur.',
        'Soutenir les ventes avec des outils concrets.',
      ],
      responsibilities: [
        'Création de contenus et campagnes.',
        'Gestion de la présence numérique.',
        'Soutien marketing aux équipes de vente.',
      ],
      kpis: [
        'Visibilité et engagement sur les canaux clés.',
        'Nombre de leads qualifiés générés.',
        'Clarté perçue du message BZ / PRIsME.',
      ],
      collaborations: [
        'Travaille avec Dany et Antoine sur les campagnes.',
        'Travaille avec Éric H. sur le positionnement PRIsME.',
        'Collabore avec les directions pour mettre en valeur les succès.',
      ],
    },
    details: {
      mandatEsprit: [
        'Faire rayonner BZ sans sur-vendre.',
        'Traduire les cadres (PRIsME, gouvernance) en messages simples.',
      ],
      responsibilitiesRaci: {
        responsible: ['Animation marketing et contenus.', 'Cohérence du message de marque.'],
        accountable: ['Positionnement global de BZ.', 'Qualité des outils de vente.'],
        consulted: ['Lancements d’offres, événements, prises de parole publiques.'],
      },
      objectives12Months: [
        'Clarifier quelques messages phares BZ / PRIsME.',
        'Structurer un kit marketing simple pour les ventes.',
      ],
      kpisCadence: [
        'Suivi mensuel des principaux indicateurs marketing.',
        'Revue régulière des campagnes avec ventes.',
      ],
      guardrails: [
        'Ne pas créer de décalage entre message et réalité.',
        'Ne pas diluer l’ADN de BZ dans le marketing.',
      ],
      interfaces: [
        'Interface avec ventes pour les besoins terrain.',
        'Interface avec les directions pour les histoires de succès.',
      ],
    },
  },
  {
    id: 'melissa',
    name: 'Mélissa Pelletier',
    title: 'Cheffe spécialiste comptabilité BZ',
    poleId: 'corporateGovernance',
    poleLabel: 'Finance & conformité',
    type: 'director',
    level: 3,
    avatar: '/MP.png',
    tagline: 'Plus haute spécialiste comptabilité chez BZ : chiffres, banques, fiscalité et contrôles.',
    whyRole: 'Assurer la qualité comptable, la fiabilité des chiffres et la conformité financière de BZ.',
    summary: {
      mandat: [
        'Produire une information financière fiable et utile.',
        'Assurer la conformité fiscale et bancaire.',
        'Soutenir le CE dans ses décisions financières.',
      ],
      responsibilities: [
        'Préparation et suivi des états financiers.',
        'Relation avec les banques et autorités fiscales.',
        'Mise en place de contrôles internes pragmatiques.',
      ],
      kpis: [
        'Qualité et ponctualité des états financiers.',
        'Résultats des revues / audits.',
        'Stabilité de la relation bancaire.',
      ],
      collaborations: [
        'Travaille avec François sur la gouvernance financière.',
        'Travaille avec LAH sur les flux administratifs liés aux opérations.',
        'Collabore avec Dany sur les décisions financières clés.',
      ],
    },
    details: {
      mandatEsprit: [
        'Être la gardienne de la qualité des chiffres.',
        'Donner une lecture claire et honnête de la situation financière.',
      ],
      responsibilitiesRaci: {
        responsible: ['Production des chiffres financiers.', 'Suivi des obligations fiscales.'],
        accountable: ['Fiabilité de l’information financière.', 'Conformité financière.'],
        consulted: ['Décisions CE impliquant financement, investissements, structure de capital.'],
      },
      objectives12Months: [
        'Renforcer quelques contrôles internes clés.',
        'Améliorer la lisibilité financière pour le CE.',
      ],
      kpisCadence: [
        'Clôtures mensuelles à temps.',
        'Revue périodique des indicateurs financiers avec le CE.',
      ],
      guardrails: [
        'Ne pas rendre les processus financiers inutiles lourds.',
        'Ne pas tolérer de zones grises sur la conformité.',
      ],
      interfaces: [
        'Interface avec les banques et comptables externes.',
        'Interface avec l’administration pour les flux financiers.',
      ],
    },
  },
  {
    id: 'lah',
    name: 'Louis-Alexandre Hébert',
    title: 'Directeur RH & opérations administratives',
    poleId: 'corporateGovernance',
    poleLabel: 'Administration & RH',
    type: 'director',
    level: 3,
    avatar: '/LAH.jpg',
    tagline: 'RH, accompagnement humain, politiques internes et opérations administratives.',
    whyRole:
      'Stabiliser les opérations administratives et faire vivre les pratiques RH avec clarté, respect et efficacité.',
    summary: {
      mandat: [
        'Diriger les ressources humaines et l’accompagnement des équipes.',
        'Piloter les opérations administratives qui soutiennent les politiques internes.',
        'Aider les gestionnaires à appliquer les règles avec simplicité et jugement.',
      ],
      responsibilities: [
        'Cadres RH, politiques internes et accompagnement des gestionnaires.',
        'Coordination des opérations administratives liées aux employés et processus internes.',
        'Clarification des rôles et responsabilisation humaine dans les changements.',
      ],
      kpis: [
        'Clarté des rôles et politiques RH.',
        'Qualité d’exécution des processus administratifs.',
        'Stabilité et autonomie des équipes.',
      ],
      collaborations: [
        'Travaille avec François sur la gouvernance corporative et les risques RH.',
        'Travaille avec Mélissa sur les flux administratifs liés aux chiffres et aux contrôles.',
        'Collabore avec Dany sur la culture, les arbitrages humains et l’amélioration continue.',
      ],
    },
    details: {
      mandatEsprit: [
        'Être le gardien terrain de l’humain dans l’administration et les politiques internes.',
        'Transformer les règles RH en comportements simples, compris et appliqués.',
      ],
      responsibilitiesRaci: {
        responsible: ['Administration RH quotidienne.', 'Application et amélioration des politiques internes.'],
        accountable: ['Clarté des rôles RH.', 'Efficacité des opérations administratives liées aux équipes.'],
        consulted: ['Décisions CE touchant les personnes, politiques, conditions de travail et changements administratifs.'],
      },
      objectives12Months: [
        'Clarifier les rituels RH et administratifs critiques.',
        'Réduire les zones grises dans l’application des politiques internes.',
      ],
      kpisCadence: [
        'Revue mensuelle des enjeux RH/admin avec François et Dany.',
        'Suivi des irritants administratifs qui ralentissent les équipes.',
      ],
      guardrails: [
        'Ne pas transformer les politiques en bureaucratie.',
        'Ne pas déconnecter la RH de la réalité terrain.',
      ],
      interfaces: [
        'Interface avec les gestionnaires pour les enjeux RH.',
        'Interface avec l’administration et la comptabilité pour les processus internes.',
      ],
    },
  },
];

export const hierarchicalLinks: HierarchicalLink[] = [
  { from: 'dany', to: 'francois' },
  { from: 'dany', to: 'jp' },
  { from: 'dany', to: 'francis' },
  { from: 'dany', to: 'edurand' },
  { from: 'dany', to: 'rdelisle' },
  { from: 'edurand', to: 'guillaume' },
  { from: 'dany', to: 'antoine' },
  { from: 'dany', to: 'rosalie' },
  { from: 'francois', to: 'melissa' },
  { from: 'francois', to: 'lah' },
];

export const functionalLinks: FunctionalLink[] = [
  { from: 'jm', to: 'jp' },
  { from: 'eh', to: 'jp' },
  { from: 'eh', to: 'francis' },
  { from: 'francis', to: 'jp' },
  { from: 'rosalie', to: 'eh' },
  { from: 'antoine', to: 'francis' },
  { from: 'rdelisle', to: 'jp' },
  { from: 'rdelisle', to: 'edurand' },
  { from: 'melissa', to: 'lah' },
];

export const comiteGouvernanceTI: Committee = {
  id: 'comiteGouvernanceTI',
  name: 'Comité Gouvernance TI',
  presidentId: 'jp',
  memberIds: ['francis', 'eh', 'jm', 'vincent'],
  arbitrageFinalId: 'dany',
  mandate:
    'Prioriser les enjeux TI majeurs, piloter l’adoption de PRIsME, arbitrer les ponts N2↔N3 et s’assurer que la capacité et la charge restent maîtrisées.',
  rules: [
    'Pas de gestion de tickets : uniquement décisions, owners et échéances.',
    'Chaque point doit être lié à un impact client ou à un risque pour BZ.',
    'Les décisions sont documentées et communiquées aux équipes concernées.',
  ],
};

export const poleOrder: PoleId[] = [
  'growth',
  'corporateGovernance',
  'serviceOps',
  'solutionsPrisme',
  'programmingAI',
  'cloudLicences',
];

export const polesMetadata: Record<
  PoleId,
  {
    label: string;
    description: string;
    colorClass: string;
    ceOwnerId: Person['id'] | null;
  }
> = {
  growth: {
    label: 'Croissance & Marché',
    description: 'Pipeline, ventes, marketing et positionnement.',
    colorClass: 'border-emerald-400/60',
    ceOwnerId: 'dany',
  },
  corporateGovernance: {
    label: 'Gouvernance corporative',
    description: 'Finance, RH, conformité et flux de revenus.',
    colorClass: 'border-sky-400/60',
    ceOwnerId: 'francois',
  },
  serviceOps: {
    label: 'Service TI & Opérations',
    description: 'SLA, N1/N2/SAC et moteur récurrent.',
    colorClass: 'border-indigo-400/60',
    ceOwnerId: 'dany',
  },
  solutionsPrisme: {
    label: "Solutions d'affaires & Cybersécurité (PRIsME)",
    description: 'N3, architecture, sécurité avancée et cadre PRIsME.',
    colorClass: 'border-violet-400/60',
    ceOwnerId: 'dany',
  },
  programmingAI: {
    label: 'Programmation & IA',
    description: 'Build, automatisation, agents et intégrations.',
    colorClass: 'border-fuchsia-400/60',
    ceOwnerId: 'dany',
  },
  cloudLicences: {
    label: 'Infonuagique / Cloud & Licences',
    description: 'Cloud ops, standards, coûts et contrats de licences.',
    colorClass: 'border-amber-400/60',
    ceOwnerId: 'dany',
  },
};

export function getPersonById(id: Person['id']): Person | undefined {
  return persons.find((p) => p.id === id);
}
